/**
 * Sector layer — pure simulation (see types.ts for the shapes and the boundary).
 *
 * Deterministic: the same seed and the same orders at the same days give the
 * same sector, battle for battle (battle seeds derive from the sector seed).
 * State in, new state out; nothing here is mutated.
 */

import { addWrecks, wrecksOf, salvage } from './salvage';
import { rememberAttack, memorySummary, refitKitFor, facingFor, garrisonWatches } from './garrison';
import { terrainFingerprint } from './terrainPrint';
import type { RunningBattle } from './types';
import { modelFromSetup, modelFromMatch, runTo, runToEnd, toOutcome, type ModelBattle, type ModelInput } from './battleModel';
import type { GameState } from '../types';
import { chronicleFromModel } from './chronicle';
import { SeededRandom } from '../core/random';
import { dcos, dsin } from '../core/dmath';
import { kitPreset, type KitPresetKey } from '../core/unitModifiers';
import { SCOUT_DAYS, defenderAwareOf, initialKnowledge, observe, observeBattle, observeSystem, factionsHearing, observeSquad, stealPicture } from './knowledge';
import type { PlanetArchetype, PlanetState } from '../types';
import type {
  BattleOpening, BattleOutcome, BattleRecord, BattleSetup, BattleSide, Doctrine, Faction, FactionId, Lane, Order, SectorEvent, SectorRules, SectorState,
  SectorSystem, Squad, SquadId, SquadUnit, Stance, SystemId, SystemScars, UnitId,
} from './types';
import type { AlertLevel } from '../types';
import { dhypot } from '../core/dmath';
import { scenarioResult } from './scenario';
import { addTraces, arrivalTrace, battleTraces, heldTrace, pruneTraces } from './signatures';
import { refreshWeather, systemWeather } from './weather';
import { commsDelay, commsLanes, deliverReports, squadWhere } from './comms';

const ALERT_LEVELS: AlertLevel[] = ['unaware', 'suspicious', 'alert'];

/** The garrison's alert floor today: the level the last battle left, stepped down once per `alertDecayDays`. */
export function alertFloor(sys: SectorSystem, day: number, decayDays: number): AlertLevel {
  if (!sys.alert) return 'unaware';
  const steps = decayDays > 0 ? Math.floor((day - sys.alert.since) / decayDays) : 0;
  const i = Math.max(0, ALERT_LEVELS.indexOf(sys.alert.level) - steps);
  return ALERT_LEVELS[i];
}

// -----------------------------------------------------------------------------
// Generation
// -----------------------------------------------------------------------------

export interface SectorOptions {
  systems?: number;          // default 5
  factions?: Array<{ id: FactionId; name: string; color: string; doctrine?: Partial<Doctrine>; kit?: KitPresetKey }>;
  squadsPerFaction?: number; // default 2
  unitsPerSquad?: number;    // default 3
  /** Days per unit of map distance (lane length ÷ speed). Default 1 day per 100 units. */
  travelDaysPer100?: number;
  planetCount?: number;      // default 6
  cbpPerUnit?: number;       // default 12
  /** Dials; anything unset is today's behaviour (DEFAULT_RULES). */
  rules?: Partial<SectorRules>;
  /** Credits each faction starts with (musters spend it). Default 0. */
  startingSupply?: number;
}

export const DEFAULT_RULES: SectorRules = {
  knockbackMultiplier: 1,
  fallDamageMultiplier: 1,
  reinforceDays: 0,
  squadCost: 36,
  maxSquads: 3,
  incomePerSystemPerDay: 0,
  healPerDay: 0,
  battleDaysPerRound: 0,
  squadUnits: 3,
  secureDays: 2,
  sneakTravelMult: 1.5,
  openingBias: 0,
  alertDecayDays: 3,
  prepareDays: 2,
  prepareCost: 20,
  maxStructuresPerSystem: 3,
  commsLanes: Infinity,
  reportHalfLifeDays: 0,
  dronesPerSquad: 1,
  droneCost: 4,
  extraction: false,
  callIns: false,
  reserves: false,
  kitCost: { standard: 0, scout: 6, assault: 8, entrenched: 8, mobile: 8, infiltrator: 8, sentinel: 6 },
  adaptiveGarrisons: false,
  valueSystems: false,
  marksCrossDown: true,
  signatures: false,
  weatherWindows: false,
  commsReach: false,
  commsDaysPerLane: 0.5,
  satellites: false,
  decoys: false,
  decoyCost: 4,
  decoyDays: 6,
  surrogateFirst: false,
  stealPicture: false,
  salvage: false,
  ammunition: false,
  ammoPerUnit: 8,
  ammoCost: 1,
  battleCapBase: 6,
  battleCapPerPlanet: 1,
};

/** 56.15: how many units a side may field in a battle at `sys` (Infinity when the cap is off). */
export function battleCap(rules: SectorRules, sys: Pick<SectorSystem, 'planetCount'>): number {
  if (!(rules.battleCapBase > 0)) return Infinity;
  return Math.max(rules.battleCapBase, Math.round(rules.battleCapPerPlanet * sys.planetCount));
}

const SYSTEM_NAMES = ['Anvil', 'Bracken', 'Cinder', 'Dorsa', 'Erebus', 'Faro', 'Gale', 'Hollow', 'Ister', 'Juno', 'Kestrel', 'Lumen'];
const ARCHETYPES: PlanetArchetype[] = ['thalia', 'bruma', 'tephra', 'murex', 'pallor', 'pelago', 'ochrus', 'viridis', 'sonora'];
const UNIT_NAMES = ['Ash', 'Bel', 'Cor', 'Dax', 'Eli', 'Fen', 'Gil', 'Hal', 'Ida', 'Jun', 'Kai', 'Lio', 'Mae', 'Nim', 'Oda', 'Pim', 'Quin', 'Rae', 'Sol', 'Tam'];

export const DEFAULT_DOCTRINE: Doctrine = { skill: 'hard', personality: 'balanced', jump: 'careful', flow: 'aware', rounds: 30, goLoudRoundsLeft: 0 };

export function createSector(seed: string, opts: SectorOptions = {}): SectorState {
  const rng = new SeededRandom(`${seed}-sector`);
  const n = Math.max(2, opts.systems ?? 5);
  const factionDefs = opts.factions ?? [
    { id: 'blue', name: 'Blue', color: '#4a90d9' },
    { id: 'red', name: 'Red', color: '#d9534f' },
  ];
  const perFaction = opts.squadsPerFaction ?? 2;
  const unitsPerSquad = opts.unitsPerSquad ?? 3;
  const per100 = opts.travelDaysPer100 ?? 1;

  // Systems scattered on a 1000×600 map with a minimum spacing
  const systems: SectorSystem[] = [];
  for (let i = 0; i < n; i++) {
    let x = 0, y = 0;
    for (let tries = 0; tries < 50; tries++) {
      x = Math.round(rng.range(80, 920)); y = Math.round(rng.range(80, 520));
      if (systems.every(s => dhypot(s.x - x, s.y - y) > 160)) break;
    }
    const size = rng.next();
    systems.push({
      id: `sys-${i}`,
      name: SYSTEM_NAMES[i % SYSTEM_NAMES.length],
      x, y,
      mapSeed: `${seed}-map-${i}`,
      planetCount: opts.planetCount ?? 6,
      planetSize: size < 0.25 ? 'small' : size < 0.85 ? 'mixed' : 'large',
      layout: 'random',
      hazardDensity: rng.next() < 0.3 ? 'some' : 'none',
      archetype: ARCHETYPES[rng.int(0, ARCHETYPES.length - 1)],
      owner: null,
      garrisoned: false,
      structures: [],
      scars: { battles: 0, craterVertices: 0, massLostFraction: 0 },
    });
  }

  // Lanes: a minimum spanning tree (connected), then each system's nearest two neighbours
  const lanes: Lane[] = [];
  const has = (a: string, b: string) => lanes.some(l => (l.a === a && l.b === b) || (l.a === b && l.b === a));
  const dist = (a: SectorSystem, b: SectorSystem) => dhypot(a.x - b.x, a.y - b.y);
  const addLane = (a: SectorSystem, b: SectorSystem) => { if (a.id !== b.id && !has(a.id, b.id)) lanes.push({ a: a.id, b: b.id, days: Math.max(0.5, Math.round(dist(a, b) / 100 * per100 * 2) / 2) }); };
  const inTree = new Set<string>([systems[0].id]);
  while (inTree.size < systems.length) {
    let best: [SectorSystem, SectorSystem] | null = null; let bd = Infinity;
    for (const a of systems) if (inTree.has(a.id)) for (const b of systems) if (!inTree.has(b.id)) { const d = dist(a, b); if (d < bd) { bd = d; best = [a, b]; } }
    if (!best) break;
    addLane(best[0], best[1]); inTree.add(best[1].id);
  }
  for (const a of systems) {
    const near = systems.filter(b => b.id !== a.id).sort((p, q) => dist(a, p) - dist(a, q)).slice(0, 2);
    for (const b of near) addLane(a, b);
  }

  // Homes: the two systems farthest apart for the first two factions, then farthest from all homes
  const factions: Faction[] = [];
  const homes: SectorSystem[] = [];
  for (let f = 0; f < factionDefs.length; f++) {
    let pick: SectorSystem = systems[0];
    if (f === 0) {
      let bd = -1;
      for (const a of systems) for (const b of systems) { const d = dist(a, b); if (d > bd) { bd = d; pick = a; } }
    } else {
      let bd = -1;
      for (const s of systems) { if (homes.includes(s)) continue; const d = Math.min(...homes.map(h => dist(h, s))); if (d > bd) { bd = d; pick = s; } }
    }
    homes.push(pick);
    pick.owner = factionDefs[f].id;
    pick.garrisoned = true;
    factions.push({
      id: factionDefs[f].id, name: factionDefs[f].name, color: factionDefs[f].color, home: pick.id,
      supply: opts.startingSupply ?? 0, doctrine: { ...DEFAULT_DOCTRINE, ...(factionDefs[f].doctrine ?? {}) },
      kit: kitPreset(factionDefs[f].kit ?? 'standard'), lastMusterDay: 0,
    });
  }

  // Squads muster at home
  const squads: Squad[] = [];
  let unitCounter = 0;
  for (let f = 0; f < factionDefs.length; f++) {
    for (let s = 0; s < perFaction; s++) {
      squads.push(makeSquad(factions[f], s + 1, unitsPerSquad, unitCounter, homes[f].id, opts.cbpPerUnit ?? 12, opts.rules?.dronesPerSquad ?? DEFAULT_RULES.dronesPerSquad, opts.rules?.ammunition ? (opts.rules.ammoPerUnit ?? DEFAULT_RULES.ammoPerUnit) : undefined));
      unitCounter += unitsPerSquad;
    }
  }

  const rules: SectorRules = { ...DEFAULT_RULES, ...(opts.rules ?? {}), squadUnits: unitsPerSquad };
  const bare: SectorState = { seed, options: { ...(opts as Record<string, unknown>) }, orderLog: [], day: 0, turn: 0, rules, knowledge: {}, factions, systems, lanes, squads, battles: [], log: [], pending: null, victor: null };
  const knowledge: SectorState['knowledge'] = {};
  for (const f of factions) knowledge[f.id] = initialKnowledge(bare, f.id);
  return observe(refreshWeather({ ...bare, knowledge }));   // X6: the first day's sky
}

function makeSquad(faction: Faction, index: number, unitsPerSquad: number, firstUnit: number, at: SystemId, supply: number, drones: number, ammo?: number): Squad {
  const units: SquadUnit[] = [];
  for (let u = 0; u < unitsPerSquad; u++) {
    const n = firstUnit + u;
    units.push({
      id: `u-${n}`,
      name: UNIT_NAMES[n % UNIT_NAMES.length] + (n >= UNIT_NAMES.length ? `-${Math.floor(n / UNIT_NAMES.length)}` : ''),
      health: 100, alive: true, kit: { ...faction.kit, launcher: { ...faction.kit.launcher }, suit: { ...faction.kit.suit }, equipment: [...faction.kit.equipment], traits: { ...faction.kit.traits } },
      kills: 0, battles: 0,
    });
  }
  return {
    id: `${faction.id}-${index}`, faction: faction.id, name: `${faction.name} ${index}`, units, doctrine: faction.doctrine,
    supply, drones, ...(ammo !== undefined ? { ammo } : {}), at, transit: null, order: { kind: 'hold' }, stance: 'assault',
  };
}

// -----------------------------------------------------------------------------
// Queries
// -----------------------------------------------------------------------------

export function systemById(state: SectorState, id: SystemId): SectorSystem {
  const s = state.systems.find(x => x.id === id);
  if (!s) throw new Error(`no system ${id}`);
  return s;
}

export function squadById(state: SectorState, id: SquadId): Squad {
  const s = state.squads.find(x => x.id === id);
  if (!s) throw new Error(`no squad ${id}`);
  return s;
}

export function laneBetween(state: SectorState, a: SystemId, b: SystemId): Lane | null {
  return state.lanes.find(l => (l.a === a && l.b === b) || (l.a === b && l.b === a)) ?? null;
}

export function neighbours(state: SectorState, id: SystemId): SystemId[] {
  return state.lanes.filter(l => l.a === id || l.b === id).map(l => (l.a === id ? l.b : l.a));
}

/** Shortest lane path by days (Dijkstra over a handful of systems). */
export function pathBetween(state: SectorState, from: SystemId, to: SystemId): SystemId[] | null {
  if (from === to) return [from];
  const best = new Map<SystemId, number>([[from, 0]]);
  const prev = new Map<SystemId, SystemId>();
  const open = new Set<SystemId>([from]);
  while (open.size) {
    let cur: SystemId | null = null; let cd = Infinity;
    for (const id of open) { const d = best.get(id)!; if (d < cd) { cd = d; cur = id; } }
    if (!cur) break;
    open.delete(cur);
    if (cur === to) break;
    for (const nb of neighbours(state, cur)) {
      const lane = laneBetween(state, cur, nb)!;
      const nd = cd + lane.days;
      if (nd < (best.get(nb) ?? Infinity)) { best.set(nb, nd); prev.set(nb, cur); open.add(nb); }
    }
  }
  if (!best.has(to)) return null;
  const path: SystemId[] = [to];
  while (path[0] !== from) path.unshift(prev.get(path[0])!);
  return path;
}

export function squadsAt(state: SectorState, systemId: SystemId): Squad[] {
  return state.squads.filter(s => s.at === systemId);
}

export function aliveUnits(squad: Squad): SquadUnit[] {
  return squad.units.filter(u => u.alive);
}

// -----------------------------------------------------------------------------
// Orders
// -----------------------------------------------------------------------------

export function issueOrder(state: SectorState, squadId: SquadId, order: Order): SectorState {
  const squad = squadById(state, squadId);
  if (order.kind === 'move' && !state.systems.some(s => s.id === order.to)) throw new Error(`no system ${order.to}`);
  // X21 (rules.commsReach): an order to a squad beyond a relay is in flight until it arrives; beyond reach, it is lost.
  // Logged either way (the order log replays the queue), then validated and applied as if given when it arrives.
  if (state.rules.commsReach) {
    const where = squadWhere(squad);
    const delay = commsDelay(state, squad.faction, where);
    if (delay !== 0) {
      const logged: SectorState = { ...state, orderLog: [...state.orderLog, { day: state.day, squadId, order }] };
      if (delay === null) return { ...logged, log: [...logged.log, { day: state.day, kind: 'order', text: `${squad.name}: no comms reach ${systemById(state, where).name} — the order is lost`, squadId, faction: squad.faction }] };
      const lanes = commsLanes(state, squad.faction, where) ?? 0;
      return {
        ...logged,
        ordersInFlight: [...(state.ordersInFlight ?? []).filter(o => o.squadId !== squadId), { squadId, order, issued: state.day, arrives: state.day + delay, lanes }],
        log: [...logged.log, { day: state.day, kind: 'order', text: `${squad.name}: order en route over ${lanes} lane${lanes === 1 ? '' : 's'}, arrives day ${(state.day + delay).toFixed(1)}`, squadId, faction: squad.faction }],
      };
    }
  }
  return applyOrderNow({ ...state, orderLog: [...state.orderLog, { day: state.day, squadId, order }] }, squadId, order);
}

/** X21: deliver every order in flight whose day has come (validated now; a refusal is logged, not fatal). */
function deliverOrders(state: SectorState): SectorState {
  if (!state.ordersInFlight?.length) return state;
  const due = state.ordersInFlight.filter(o => o.arrives <= state.day + EPS);
  if (!due.length) return state;
  let s: SectorState = { ...state, ordersInFlight: state.ordersInFlight.filter(o => o.arrives > state.day + EPS) };
  for (const o of due) {
    const q = s.squads.find(x => x.id === o.squadId);
    if (!q || !aliveUnits(q).length) continue;
    try { s = applyOrderNow(s, o.squadId, o.order); }
    catch (err) { s = { ...s, log: [...s.log, { day: s.day, kind: 'order', text: `${q.name}: the order that arrived is refused — ${err instanceof Error ? err.message : String(err)}`, squadId: q.id, faction: q.faction }] }; }
  }
  return s;
}

/** An order applied now: validated against this state and stamped (the order log is the caller's). */
function applyOrderNow(state: SectorState, squadId: SquadId, order: Order): SectorState {
  const squad = squadById(state, squadId);
  let stamped: Order = order;
  let stance: Stance = squad.stance;
  if (order.kind === 'scout') {
    if (squad.at === null) throw new Error(`${squadId} cannot scout while in transit`);
    if (!neighbours(state, squad.at).includes(order.target)) throw new Error(`${order.target} is not adjacent to ${squad.at}`);
    // 56.16a: a look at what is already watched tells nothing — say why instead of taking the days
    const watched = alreadyWatched(state, squad.faction, order.target);
    if (watched) throw new Error(`${systemById(state, order.target).name} is already watched: ${watched}`);
    stamped = { kind: 'scout', target: order.target, since: state.day };
  }
  if (order.kind === 'secure') {
    if (squad.at === null) throw new Error(`${squadId} cannot secure while in transit`);
    if (systemById(state, squad.at).owner === squad.faction) throw new Error(`${squad.faction} already holds ${squad.at}`);
    if (squadsAt(state, squad.at).some(x => x.faction !== squad.faction && aliveUnits(x).length > 0)) throw new Error(`${squad.at} is contested`);
    stamped = { kind: 'secure', since: state.day };
  }
  if (order.kind === 'attack') {
    if (squad.at === null) throw new Error(`${squadId} cannot attack while in transit`);
    if (!squadsAt(state, squad.at).some(x => x.faction !== squad.faction && aliveUnits(x).length > 0)) throw new Error(`no enemy at ${systemById(state, squad.at).name} to attack`);
  }
  if (order.kind === 'move') stance = order.stance ?? 'assault';
  // 62.17 step 4: ordering a squad out of a model battle withdraws its side (pursuit losses; the battle lands now)
  let withdrawn: SectorState | null = null;
  if (order.kind === 'move' && squad.at !== null && order.to !== squad.at) {
    const fight = modelBattleAt(state, squad.at, squad.faction);
    if (fight && fight.r.setup.sides[fight.side].squadIds.includes(squadId)) withdrawn = inputToRunning(state, fight.r, { kind: 'withdraw', side: fight.side });
  }
  if (withdrawn) state = withdrawn;
  let factions = state.factions;
  if (order.kind === 'prepare') {
    if (squad.at === null) throw new Error(`${squadId} cannot build while in transit`);
    const sys = systemById(state, squad.at);
    if (sys.owner !== squad.faction) throw new Error(`${squad.faction} does not hold ${squad.at}`);
    if (sys.structures.length >= state.rules.maxStructuresPerSystem) throw new Error(`${squad.at} has no room to build`);
    const faction = state.factions.find(f => f.id === squad.faction)!;
    if (faction.supply < state.rules.prepareCost) throw new Error(`${squad.faction} cannot afford to build (${faction.supply} < ${state.rules.prepareCost})`);
    // paid at the order: a commitment
    factions = state.factions.map(f => (f.id === squad.faction ? { ...f, supply: f.supply - state.rules.prepareCost } : f));
    stamped = { kind: 'prepare', build: order.build, since: state.day };
  }
  const text = order.kind === 'hold'
    ? `${squad.name} holds`
    : order.kind === 'move'
      ? `${squad.name} ordered to ${systemById(state, order.to).name}${stance === 'sneak' ? ' (sneaking)' : ''}`
      : order.kind === 'scout'
        ? `${squad.name} scouts ${systemById(state, order.target).name}`
        : order.kind === 'secure'
          ? `${squad.name} secures ${systemById(state, squad.at!).name}`
          : order.kind === 'attack'
            ? `${squad.name} attacks at ${systemById(state, squad.at!).name}`
            : `${squad.name} builds a ${order.build} at ${systemById(state, squad.at!).name}`;
  return {
    ...state,
    factions,
    squads: state.squads.map(s => (s.id === squadId ? { ...s, order: stamped, stance } : s)),
    log: [...state.log, { day: state.day, kind: 'order', text, squadId, faction: squad.faction }],
  };
}

// -----------------------------------------------------------------------------
// The clock
// -----------------------------------------------------------------------------

const EPS = 1e-9;

/** After an undecided battle, hostile squads in one system fight again only after this many days (one per turn). */
export const BATTLE_COOLDOWN_DAYS = 1;

/**
 * Advance sector time by `days`. Squads with a move order depart along the
 * shortest path; arrivals happen at their exact day. If an arrival puts
 * hostile squads in one system, the clock stops at that moment with
 * `state.pending` set — resolve it, `applyBattleOutcome`, and advance again.
 * Returns the state with the clock as far as it got.
 */
export function advanceSector(state: SectorState, days: number): SectorState {
  return settleScenario(advanceSectorClock(state, days));   // a scenario records its result on every way out
}

function advanceSectorClock(state: SectorState, days: number): SectorState {
  if (state.pending || state.victor) return state;
  if (days <= 0) { const due = findDueBattle(state); return due ? takeDue(state, due).state : state; }
  // 62.4: an attack order fights now (checked only when one exists, so every other campaign is unchanged)
  let s = state;
  if (state.squads.some(q => q.order.kind === 'attack')) { const due = findDueBattle(state); if (due) { const t = takeDue(state, due); if (!t.started) return t.state; s = t.state; } }
  // a scenario's day limit stops the clock (scenario.ts)
  const target = Math.min(state.day + days, state.scenario ? state.scenario.dayLimit : Infinity);
  let guard = 0;
  while (s.day < target - EPS && guard++ < 10_000) {
    // 0. Battles already over (a withdrawal ends one at once) land before anyone departs
    if ((s.running ?? []).some(r => r.endsDay <= s.day + EPS)) { s = landDue(s); if (s.victor) return s; }
    // 0b. X21: orders whose day has come reach their squads
    s = deliverOrders(s);
    // 1. Departures: every idle squad with a move order starts its next hop
    s = departAll(s);
    // 2. The next event inside this step: an arrival, a stand-off's cooldown ending, a muster day
    const nextArrival = Math.min(target, ...s.squads.filter(q => q.transit).map(q => q.transit!.arrives));
    const nextRefight = Math.min(target, ...standoffSystems(s).map(sys => refightDay(sys)).filter(d => d > s.day + EPS));
    const nextMuster = s.rules.reinforceDays > 0
      ? Math.min(target, ...s.factions.map(f => f.lastMusterDay + s.rules.reinforceDays).filter(d => d > s.day + EPS))
      : target;
    const nextScout = Math.min(target, ...s.squads.filter(q => q.order.kind === 'scout' && q.at !== null).map(q => (q.order as { since: number }).since + SCOUT_DAYS).filter(d => d > s.day + EPS));
    const nextSecure = Math.min(target, ...s.squads.filter(q => q.order.kind === 'secure' && q.at !== null).map(q => (q.order as { since: number }).since + s.rules.secureDays).filter(d => d > s.day + EPS));
    const nextPrepare = Math.min(target, ...s.squads.filter(q => q.order.kind === 'prepare' && q.at !== null).map(q => (q.order as { since: number }).since + s.rules.prepareDays).filter(d => d > s.day + EPS));
    const nextLanding = Math.min(target, ...(s.running ?? []).map(r => r.endsDay).filter(d => d > s.day + EPS));
    const nextOrder = Math.min(target, ...(s.ordersInFlight ?? []).map(o => o.arrives).filter(d => d > s.day + EPS));   // X21
    const t = Math.min(target, nextArrival, nextRefight, nextMuster, nextScout, nextSecure, nextPrepare, nextLanding, nextOrder);
    const dt = t - s.day;
    s = { ...s, day: t };
    // 2b. Time passing: income and healing
    s = passTime(s, dt);
    // 2c. Battles under way that end at t land first (the fight is over before anyone arrives)
    if ((s.running ?? []).length) { s = landDue(s); if (s.victor) return s; }
    // 3. Arrivals at exactly t, then musters due at t, then scout reports due at t
    const arriving = s.squads.filter(q => q.transit && q.transit.arrives <= t + EPS);
    for (const q of arriving) s = arrive(s, q.id);
    s = deliverOrders(s);   // X21: orders arriving at t reach squads that are here now
    s = musterAll(s);
    s = completeScouts(s);
    s = completeSecures(s);
    s = completePrepares(s);
    // 3b. Everyone records what they can see now (X1: faded traces drop first, so no report carries one; X6: today's sky)
    s = pruneTraces(s);
    s = refreshWeather(s);
    s = deliverReports(s);   // X21: reports whose day has come
    s = observe(s);
    // 4. A battle due? Stop here — or, surrogate-first (X9), start it in the model and run on
    const due = findDueBattle(s);
    if (due) { const t = takeDue(s, due); if (!t.started) return t.state; s = t.state; continue; }
    if (!arriving.length && nextArrival >= target && nextRefight >= target && nextMuster >= target && nextScout >= target && nextSecure >= target && nextPrepare >= target && nextLanding >= target && nextOrder >= target) break;
  }
  return s;
}

/** A scenario decided (its target taken, the home lost, the day limit reached) records its result and ends the sector. */
export function settleScenario(s: SectorState): SectorState {
  const sc = s.scenario;
  if (!sc || sc.result) return s;
  const r = scenarioResult(s);
  if (!r) return s;
  return { ...s, scenario: { ...sc, result: r }, victor: s.victor ?? (r === 'won' ? sc.player : sc.enemy), log: [...s.log, { day: s.day, kind: 'order', text: r === 'won' ? `${sc.title}: won on day ${s.day.toFixed(1)}` : `${sc.title}: lost${s.day >= sc.dayLimit - 1e-9 ? ' — the day limit' : ''}` }] };
}

/**
 * X9 at L2: a due battle pends for the host's decision (today), or — with `surrogateFirst` and the model on — starts
 * as a surrogate at once (decision 105: battles nobody enters run as surrogates; entering one later spins it up full).
 */
function takeDue(state: SectorState, due: BattleSetup): { state: SectorState; started: boolean } {
  if (state.rules.surrogateFirst && state.rules.battleModel) return { state: startModelBattle({ ...state, pending: due }), started: true };
  return { state: { ...state, pending: due }, started: false };
}

/** The day a stand-off at `sys` may fight again: the cooldown, or the last battle's own duration, whichever is later. */
function refightDay(sys: SectorSystem): number {
  return Math.max((sys.lastBattleDay ?? -Infinity) + BATTLE_COOLDOWN_DAYS, sys.busyUntil ?? -Infinity);
}

/** Income per held system (scaled by intact mass) and healing at home, for `dt` days. */
function passTime(state: SectorState, dt: number): SectorState {
  if (dt <= 0) return state;
  const { incomePerSystemPerDay, healPerDay } = state.rules;
  let s = state;
  if (incomePerSystemPerDay > 0) {
    s = {
      ...s,
      factions: s.factions.map(f => {
        const held = s.systems.filter(x => x.owner === f.id);
        const income = held.reduce((a, x) => a + incomePerSystemPerDay * (1 + 0.5 * x.structures.filter(b => b.kind === 'ecotourism').length) * Math.max(0, 1 - x.scars.massLostFraction), 0) * dt;
        return { ...f, supply: f.supply + income };
      }),
    };
  }
  // X15: where a squad rests on a held system with wrecks, it salvages them — a few a day, their worth to the holder's supply
  if (s.rules.salvage && s.systems.some(x => x.owner && x.wrecks && Object.keys(x.wrecks).length)) {
    let factions = s.factions, systems = s.systems;
    const log = [...s.log];
    for (const sys of s.systems) {
      if (!sys.owner || !sys.wrecks || !Object.keys(sys.wrecks).length) continue;
      if (!s.squads.some(q => q.faction === sys.owner && q.at === sys.id && aliveUnits(q).length > 0 && restsHere(s, sys.owner!, sys.id))) continue;
      const r = salvage(sys, s.rules, dt);
      if (!r.taken) continue;
      factions = factions.map(f => (f.id === sys.owner ? { ...f, supply: f.supply + r.supply } : f));
      systems = systems.map(x => (x.id === sys.id ? { ...x, wrecks: r.wrecks } : x));
      log.push({ day: s.day, kind: 'order', text: `Salvage at ${sys.name}: ${r.taken} wreck${r.taken === 1 ? '' : 's'} for ${r.supply.toFixed(1)} supply`, systemId: sys.id, faction: sys.owner });
    }
    s = { ...s, factions, systems, log };
  }
  // 62.3: a squad resting at home or a depot is restocked to its free drone count (what supply bought beyond it stays)
  if (s.rules.dronesPerSquad > 0 && s.squads.some(q => q.drones < s.rules.dronesPerSquad)) {
    s = { ...s, squads: s.squads.map(q => (q.drones < s.rules.dronesPerSquad && q.at !== null && restsHere(s, q.faction, q.at) ? { ...q, drones: s.rules.dronesPerSquad } : q)) };
  }
  if (healPerDay > 0) {
    s = {
      ...s,
      squads: s.squads.map(q => {
        if (q.at === null || !restsHere(s, q.faction, q.at)) return q;
        return { ...q, units: q.units.map(u => (u.alive && u.health < 100 ? { ...u, health: Math.min(100, u.health + healPerDay * dt) } : u)) };
      }),
    };
  }
  return s;
}

/**
 * 62.3 (decision 63, "more from supply"): buy `count` drones for a squad resting at home or a depot, at
 * `rules.droneCost` each from the faction's supply. Throws when the squad is elsewhere or the supply is short.
 */
export function buyDrones(state: SectorState, squadId: SquadId, count = 1): SectorState {
  const squad = squadById(state, squadId);
  if (!(count >= 1)) throw new Error('buy at least one drone');
  if (squad.at === null || !restsHere(state, squad.faction, squad.at)) throw new Error(`${squad.name} can only take on drones at home or a depot`);
  const faction = state.factions.find(f => f.id === squad.faction)!;
  const cost = count * state.rules.droneCost;
  if (faction.supply < cost) throw new Error(`${squad.faction} cannot afford ${count} drone${count === 1 ? '' : 's'} (${faction.supply} < ${cost})`);
  return {
    ...state,
    factions: state.factions.map(f => (f.id === faction.id ? { ...f, supply: f.supply - cost } : f)),
    squads: state.squads.map(q => (q.id === squadId ? { ...q, drones: q.drones + count } : q)),
    log: [...state.log, { day: state.day, kind: 'order', text: `${squad.name} takes on ${count} drone${count === 1 ? '' : 's'}`, squadId, systemId: squad.at, faction: squad.faction }],
  };
}

/** X2 at L2: a faction marks a system — the one to hit (or to shell, `fire`); it crosses down into the next battle there. */
export function markSystem(state: SectorState, faction: FactionId, systemId: SystemId, fire = false): SectorState {
  const sys = systemById(state, systemId);
  const marks = (state.marks ?? []).filter(m => !(m.faction === faction && m.systemId === systemId));
  return {
    ...state,
    marks: [...marks, { faction, systemId, fire, day: state.day }],
    log: [...state.log, { day: state.day, kind: 'order', text: `${factionName(state, faction)} marks ${sys.name}${fire ? ' for the guns' : ''}`, systemId, faction }],
  };
}

/** Clear a faction's mark on a system. */
export function unmarkSystem(state: SectorState, faction: FactionId, systemId: SystemId): SectorState {
  return { ...state, marks: (state.marks ?? []).filter(m => !(m.faction === faction && m.systemId === systemId)) };
}

/**
 * X20: supply buys **rounds**. A squad resting at home or a depot takes on `rounds` standard rounds per unit, at
 * `rules.ammoCost` supply a round (for the squad). Only with the `ammunition` rule.
 */
export function buyAmmo(state: SectorState, squadId: SquadId, rounds = 4): SectorState {
  if (!state.rules.ammunition) throw new Error('ammunition is not a quantity in this sector (the rule is off)');
  const squad = squadById(state, squadId);
  if (!(rounds >= 1)) throw new Error('buy at least one round');
  if (squad.at === null || !restsHere(state, squad.faction, squad.at)) throw new Error(`${squad.name} can only take on rounds at home or a depot`);
  const faction = state.factions.find(f => f.id === squad.faction)!;
  const cost = rounds * state.rules.ammoCost;
  if (faction.supply < cost) throw new Error(`${squad.faction} cannot afford ${rounds} round${rounds === 1 ? '' : 's'} (${faction.supply} < ${cost})`);
  return {
    ...state,
    factions: state.factions.map(f => (f.id === faction.id ? { ...f, supply: f.supply - cost } : f)),
    squads: state.squads.map(q => (q.id === squadId ? { ...q, ammo: (q.ammo ?? 0) + rounds } : q)),
    log: [...state.log, { day: state.day, kind: 'order', text: `${squad.name} takes on ${rounds} round${rounds === 1 ? '' : 's'} a unit${cost ? ` (${cost} supply)` : ''}`, squadId, systemId: squad.at, faction: squad.faction }],
  };
}

/**
 * 56.11 (the minimal sector economy): supply buys **kit** before departure. A squad resting at home or a depot is
 * refitted to a kit preset for every living unit, at `rules.kitCost[kit]` supply each from the faction. The kit
 * crosses down with the units (`BattleSide.units[].kit`) — what you brought. No shop screen; the overhaul (92) stays closed.
 */
export function refitSquad(state: SectorState, squadId: SquadId, kit: KitPresetKey): SectorState {
  const squad = squadById(state, squadId);
  if (squad.at === null || !restsHere(state, squad.faction, squad.at)) throw new Error(`${squad.name} can only refit at home or a depot`);
  const each = state.rules.kitCost[kit];
  if (each === undefined) throw new Error(`no such kit: ${kit}`);
  const faction = state.factions.find(f => f.id === squad.faction)!;
  const n = aliveUnits(squad).length;
  const cost = each * n;
  if (faction.supply < cost) throw new Error(`${squad.faction} cannot afford ${kit} for ${n} unit${n === 1 ? '' : 's'} (${faction.supply} < ${cost})`);
  return {
    ...state,
    factions: state.factions.map(f => (f.id === faction.id ? { ...f, supply: f.supply - cost } : f)),
    squads: state.squads.map(q => (q.id === squadId ? { ...q, units: q.units.map(u => (u.alive ? { ...u, kit: kitPreset(kit) } : u)) } : q)),
    log: [...state.log, { day: state.day, kind: 'order', text: `${squad.name} refits as ${kit}${cost ? ` (${cost} supply)` : ''}`, squadId, systemId: squad.at, faction: squad.faction }],
  };
}

/** Where a faction's squads heal and resupply: its home, or a system it holds with a depot (56.5). */
export function restsHere(state: SectorState, faction: FactionId, systemId: SystemId): boolean {
  const f = state.factions.find(x => x.id === faction);
  if (f?.home === systemId) return true;
  const sys = systemById(state, systemId);
  return sys.owner === faction && sys.structures.some(x => x.kind === 'depot');
}

/** Prepare orders that have run their days: the structure stands, on a seeded planet and spot; an unheld system aborts the build (the cost is sunk). */
function completePrepares(state: SectorState): SectorState {
  let s = state;
  for (const q of state.squads) {
    if (q.order.kind !== 'prepare' || q.at === null) continue;
    const sys = systemById(s, q.at);
    if (sys.owner !== q.faction) { s = { ...s, squads: s.squads.map(x => (x.id === q.id ? { ...x, order: { kind: 'hold' } } : x)) }; continue; }
    if (s.day + EPS < q.order.since + s.rules.prepareDays) continue;
    const id = s.systems.reduce((a, x) => a + x.structures.length, 0) + s.log.filter(e => e.kind === 'prepared').length;
    const rng = new SeededRandom(`${s.seed}-${sys.id}-build-${id}`);
    // the first structure picks the planet; later ones share it, so the holder's base is one planet
    const planetIndex = sys.structures[0]?.planetIndex ?? rng.int(0, sys.planetCount - 1);
    const u = rng.range(-1, 1), a = rng.range(0, Math.PI * 2), r = Math.sqrt(Math.max(0, 1 - u * u));
    const structure = { id, kind: q.order.build, planetIndex, surfaceDir: { x: r * dcos(a), y: u, z: r * dsin(a) }, builtDay: s.day, builtBy: q.faction };
    s = {
      ...s,
      systems: s.systems.map(x => (x.id === sys.id ? { ...x, structures: [...x.structures, structure] } : x)),
      squads: s.squads.map(x => (x.id === q.id ? { ...x, order: { kind: 'hold' } } : x)),
      log: [...s.log, { day: s.day, kind: 'prepared', text: `${q.name} builds a ${structure.kind} at ${sys.name}`, squadId: q.id, systemId: sys.id, faction: q.faction }],
    };
  }
  return s;
}

/** Reinforcement: a faction whose muster day has come, that still holds its home and can pay, raises a squad there. */
function musterAll(state: SectorState): SectorState {
  const { reinforceDays, squadCost, maxSquads } = state.rules;
  if (reinforceDays <= 0) return state;
  let s = state;
  for (const f of state.factions) {
    if (s.day + EPS < f.lastMusterDay + reinforceDays) continue;
    const home = systemById(s, f.home);
    const mine = s.squads.filter(q => q.faction === f.id && aliveUnits(q).length > 0);
    const faction = s.factions.find(x => x.id === f.id)!;
    if (home.owner !== f.id || mine.length >= maxSquads || faction.supply < squadCost) {
      // the day passes whether or not a squad was raised
      s = { ...s, factions: s.factions.map(x => (x.id === f.id ? { ...x, lastMusterDay: s.day } : x)) };
      continue;
    }
    const index = s.squads.filter(q => q.faction === f.id).length + s.log.filter(e => e.kind === 'muster' && e.faction === f.id).length + 1;
    const firstUnit = nextUnitNumber(s);
    const squad = makeSquad(faction, index, s.rules.squadUnits, firstUnit, f.home, DEFAULT_SQUAD_SUPPLY, s.rules.dronesPerSquad, s.rules.ammunition ? s.rules.ammoPerUnit : undefined);
    s = {
      ...s,
      factions: s.factions.map(x => (x.id === f.id ? { ...x, supply: x.supply - squadCost, lastMusterDay: s.day } : x)),
      squads: [...s.squads, squad],
      log: [...s.log, { day: s.day, kind: 'muster', text: `${squad.name} musters at ${home.name}`, squadId: squad.id, systemId: f.home, faction: f.id }],
    };
  }
  return s;
}

const DEFAULT_SQUAD_SUPPLY = 12;

/**
 * 56.16a: why a scout of `systemId` would tell `faction` nothing right now — it holds the system, has a squad standing
 * there, or looked at it today — or null when a look is worth the days.
 */
export function alreadyWatched(state: SectorState, faction: FactionId, systemId: SystemId): string | null {
  const sys = systemById(state, systemId);
  if (sys.owner === faction) return 'you hold it';
  if (state.squads.some(q => q.faction === faction && q.at === systemId && aliveUnits(q).length > 0)) return 'your squad stands there';
  const report = state.knowledge[faction]?.systems[systemId];
  if (report && state.day - report.seenDay < 0.5) return `you looked today (day ${report.seenDay.toFixed(1)})`;
  return null;
}

/** 56.16a: what a report on a system says, in words: the holder, and the enemy standing there as last seen. */
export function reportSummary(state: SectorState, faction: FactionId, systemId: SystemId): string | null {
  const k = state.knowledge[faction];
  const report = k?.systems[systemId];
  if (!k || !report) return null;
  const seen = Object.values(k.squads).filter(q => q.faction !== faction && q.at === systemId && Math.abs(q.seenDay - report.seenDay) < 1e-6 && q.strength > 0);
  const units = seen.reduce((a, q) => a + q.strength, 0);
  const holder = report.owner ? `${report.owner} holds it${report.garrisoned ? ', garrisoned' : ''}` : 'nobody holds it';
  const enemy = seen.length ? `${seen.length} enemy squad${seen.length === 1 ? '' : 's'} (${units} unit${units === 1 ? '' : 's'})` : 'no enemy standing there';
  return `${holder}; ${enemy}${report.adapted ? `; garrison ${report.adapted}` : ''}${report.via ? ` (${report.via.faction}'s report, taken day ${report.via.day.toFixed(1)})` : ''}`;
}

/** Scout orders that have run their days: the report lands in the faction's knowledge and the squad holds. */
function completeScouts(state: SectorState): SectorState {
  let s = state;
  for (const q of state.squads) {
    if (q.order.kind !== 'scout' || q.at === null) continue;
    if (s.day + EPS < q.order.since + SCOUT_DAYS) continue;
    const target = q.order.target;
    s = observeSystem(s, q.faction, target, q.at);   // X21: the scout's report travels from where it stands
    // 56.8: a probe over a system whose holder has an antenna or sensor there is itself seen — the holder learns the scout
    const tsys = systemById(s, target);
    const holderSees = !!tsys.owner && tsys.owner !== q.faction && tsys.structures.some(b => b.kind === 'antenna' || b.kind === 'sensor');
    if (holderSees) s = observeSquad(s, tsys.owner!, q.id);
    s = {
      ...s,
      squads: s.squads.map(x => (x.id === q.id ? { ...x, order: { kind: 'hold' } } : x)),
      // 56.16a: the report says what the scout learned, not only that it looked
      log: [...s.log, { day: s.day, kind: 'scout', text: `${q.name} reports on ${systemById(s, target).name}: ${reportSummary(s, q.faction, target) ?? 'nothing'}${holderSees ? ' — and was seen' : ''}`, squadId: q.id, systemId: target, faction: q.faction }],
    };
  }
  return s;
}

/** Unit ids are global and never reused: one past the highest ever issued. */
function nextUnitNumber(state: SectorState): number {
  let max = -1;
  for (const q of state.squads) for (const u of q.units) max = Math.max(max, Number(u.id.slice(2)));
  for (const b of state.battles) for (const u of b.outcome.units) max = Math.max(max, Number(u.unitId.slice(2)));
  return max + 1;
}

function departAll(state: SectorState): SectorState {
  let s = state;
  const fighting = squadsInBattle(state);
  for (const q of state.squads) {
    if (q.transit || q.at === null || q.order.kind !== 'move' || q.order.to === q.at) continue;
    if (fighting.has(q.id)) continue;   // still in a battle under way
    if (!aliveUnits(q).length) continue;
    const path = pathBetween(s, q.at, q.order.to);
    if (!path || path.length < 2) continue;
    const next = path[1];
    const lane = laneBetween(s, q.at, next)!;
    const days = lane.days * (q.stance === 'sneak' ? s.rules.sneakTravelMult : 1);
    const transit = { from: q.at, to: next, departed: s.day, arrives: s.day + days };
    s = {
      ...s,
      squads: s.squads.map(x => (x.id === q.id ? { ...x, at: null, transit, arrivedFrom: null } : x)),
      log: [...s.log, { day: s.day, kind: 'depart', text: `${q.name} departs ${systemById(s, q.at).name} for ${systemById(s, next).name}`, squadId: q.id, systemId: q.at, faction: q.faction }],
    };
  }
  return s;
}

function arrive(state: SectorState, squadId: SquadId): SectorState {
  const q = squadById(state, squadId);
  if (!q.transit) return state;
  const here = q.transit.to;
  const done = q.order.kind === 'move' && q.order.to === here;
  const sys = systemById(state, here);
  // Did the holder know this squad was coming? Judged before the arrival is observed.
  const holder = sys.owner && sys.owner !== q.faction ? sys.owner : null;
  const arrivedUnseen = holder ? !defenderAwareOf(state, holder, q.id, here, q.transit.departed) : false;
  const squad: Squad = { ...q, at: here, transit: null, order: done ? { kind: 'hold' } : q.order, arrivedUnseen, arrivedFrom: q.transit.from };
  let s: SectorState = {
    ...state,
    squads: state.squads.map(x => (x.id === squadId ? squad : x)),
    log: [...state.log, { day: state.day, kind: 'arrive', text: `${q.name} arrives at ${sys.name}${q.stance === 'sneak' ? ' unseen' : ''}`, squadId, systemId: here, faction: q.faction }],
  };
  s = addTraces(s, [arrivalTrace(s, q, here, q.transit.from)]);   // X1: the lane it came by
  // 62.17 step 4: a battle its faction is fighting here takes it in as reinforcements
  const fight = modelBattleAt(s, here, q.faction);
  if (fight && aliveUnits(squad).length) {
    const units = aliveUnits(squad).map(u => ({ unitId: u.id, name: u.name, health: u.health, kit: u.kit }));
    const setup = { ...fight.r.setup, sides: fight.r.setup.sides.map((x, i) => (i === fight.side ? { ...x, squadIds: [...x.squadIds, squad.id], units: [...x.units, ...units] } : x)) };
    s = inputToRunning(s, fight.r, { kind: 'arrive', side: fight.side, units: units.map(u => ({ unitId: u.unitId, health: u.health })) }, setup);
    s = { ...s, log: [...s.log, { day: s.day, kind: 'battle', systemId: here, faction: q.faction, text: `${q.name} joins the battle at ${sys.name}` }] };
  }
  return s;
  // Arrival takes nothing: holding takes the secure act (RFC Q3/Q9) or a battle.
}

/** Secure orders that have run their days: the system is captured and garrisoned; a contested system aborts the act. */
function completeSecures(state: SectorState): SectorState {
  let s = state;
  for (const q of state.squads) {
    if (q.order.kind !== 'secure' || q.at === null) continue;
    const contested = squadsAt(s, q.at).some(x => x.faction !== q.faction && aliveUnits(x).length > 0);
    if (contested) { s = { ...s, squads: s.squads.map(x => (x.id === q.id ? { ...x, order: { kind: 'hold' } } : x)) }; continue; }
    if (s.day + EPS < q.order.since + s.rules.secureDays) continue;
    s = capture(s, q.at, q.faction, true);
    s = {
      ...s,
      squads: s.squads.map(x => (x.id === q.id ? { ...x, order: { kind: 'hold' } } : x)),
      log: [...s.log, { day: s.day, kind: 'secured', text: `${q.name} secures ${systemById(s, q.at).name}`, squadId: q.id, systemId: q.at, faction: q.faction }],
    };
  }
  return s;
}

function capture(state: SectorState, systemId: SystemId, faction: FactionId, garrisoned: boolean): SectorState {
  const sys = systemById(state, systemId);
  // X29: a garrisoned system, or one whose antenna or sensor still stands, hands its holder's picture to the taker
  if (state.rules.stealPicture && sys.owner && sys.owner !== faction && (sys.garrisoned || sys.structures.some(b => b.kind === 'antenna' || b.kind === 'sensor'))) {
    state = stealPicture(state, faction, sys.owner, systemId).state;
  }
  // X1: a change of hands leaves how long the last holder kept it
  if (sys.owner && sys.owner !== faction) state = addTraces(state, [heldTrace(state, systemId, sys.owner, state.day - (sys.heldSince ?? 0))]);
  const s: SectorState = {
    ...state,
    // X17: a garrison's memory is its own — the new holder starts without one
    systems: state.systems.map(x => (x.id === systemId ? { ...x, owner: faction, garrisoned, ...(x.owner !== faction ? { garrisonMemory: undefined, heldSince: state.day } : {}) } : x)),
    log: [...state.log, { day: state.day, kind: 'captured', text: `${factionName(state, faction)} holds ${sys.name}`, systemId, faction }],
  };
  // the garrison's last report: a holder that loses a system learns it fell (the first scenario's playtest: Bracken
  // fell unseen and the map kept it blue for days) — through the comms net like any report, from the system itself
  const lost = sys.owner && sys.owner !== faction ? observeSystem(s, sys.owner, systemId, systemId) : s;
  const told = lost === s ? s : { ...lost, log: [...lost.log, { day: state.day, kind: 'captured' as const, text: `${sys.name} has fallen to ${factionName(state, faction)}`, systemId, faction: sys.owner! }] };
  return { ...told, victor: checkVictor(told) };
}

function factionName(state: SectorState, id: FactionId): string {
  return state.factions.find(f => f.id === id)?.name ?? id;
}

/**
 * Victory placeholder (RFC Q9): the last faction still holding its own home.
 * Works for any number of factions; the real design (victory as a layer, per
 * system and per sector) replaces it later.
 */
export function checkVictor(state: SectorState): FactionId | null {
  if (state.factions.length < 2) return null;
  const standing = state.factions.filter(f => systemById(state, f.home).owner === f.id);
  return standing.length === 1 ? standing[0].id : null;
}

// -----------------------------------------------------------------------------
// Battles: detection and the boundary
// -----------------------------------------------------------------------------

/** Systems where hostile squads share the ground. */
function standoffSystems(state: SectorState): SectorSystem[] {
  return state.systems.filter(sys => {
    const present = squadsAt(state, sys.id).filter(q => aliveUnits(q).length > 0);
    return new Set(present.map(q => q.faction)).size >= 2;
  });
}

function findDueBattle(state: SectorState): BattleSetup | null {
  for (const sys of standoffSystems(state)) {
    const attacking = squadsAt(state, sys.id).some(q => q.order.kind === 'attack' && aliveUnits(q).length > 0);
    const ready = attacking ? state.day >= (sys.busyUntil ?? -Infinity) - EPS : state.day >= refightDay(sys) - EPS;
    if (!ready) continue;
    const present = squadsAt(state, sys.id).filter(q => aliveUnits(q).length > 0);
    const factions = [...new Set(present.map(q => q.faction))];
    return buildBattleSetup(state, sys.id, factions.slice(0, 2));
  }
  return null;
}

export function buildBattleSetup(state: SectorState, systemId: SystemId, factionIds: FactionId[]): BattleSetup {
  const sys = systemById(state, systemId);
  const index = state.battles.length;
  // The holder defends as side 0 (team 0 acts first in the match): a defender's advantage, and the
  // first-mover bias alternates between campaigns instead of always favouring faction 0.
  const ordered = [...factionIds].sort((a, b) => (a === sys.owner ? -1 : b === sys.owner ? 1 : 0));
  const sides: BattleSide[] = ordered.map(fid => {
    const squads = squadsAt(state, systemId).filter(q => q.faction === fid && aliveUnits(q).length > 0);
    if (!squads.length) throw new Error(`${fid} has no squad at ${systemId}`);
    // The lane the side arrived by (RFC Q5 "where you landed"): the holder deploys freely; an arriving side
    // lands on the side of the system facing the system it came from
    const from = fid === sys.owner ? undefined : squads.map(q => q.arrivedFrom).find((f): f is SystemId => !!f && f !== systemId);
    const arrival = from ? (() => { const origin = systemById(state, from); return { from, dx: origin.x - sys.x, dy: origin.y - sys.y }; })() : undefined;
    // 56.15: the healthiest units up to the cap fight; the rest wait in reserve (ties keep squad order, so a squad is split only when it must be)
    const standing = squads.flatMap(q => aliveUnits(q).map(u => ({ squadId: q.id, unitId: u.id, name: u.name, health: u.health, kit: u.kit })));
    const cap = battleCap(state.rules, sys);
    const committed = standing.length > cap
      ? standing.map((u, i) => ({ u, i })).sort((a, b) => b.u.health - a.u.health || a.i - b.i).slice(0, cap).sort((a, b) => a.i - b.i).map(x => x.u)
      : standing;
    const committedSquads = squads.filter(q => committed.some(u => u.squadId === q.id));
    return {
      faction: fid,
      squadIds: committedSquads.map(q => q.id),
      units: committed.map(u => ({ unitId: u.unitId, name: u.name, health: u.health, kit: u.kit })),
      // 62.3: the committed squads' drones go with them (a squad split by the cap still sends all of its drones)
      drones: committedSquads.reduce((a, q) => a + q.drones, 0),
      // X20: the rounds a unit carries — the committed squads' least, so nobody fires what the squad does not have
      ...(state.rules.ammunition ? { ammo: Math.min(...committedSquads.map(q => q.ammo ?? 0)) } : {}),
      doctrine: committedSquads[0].doctrine,
      cbpPerUnit: Math.round(committedSquads.reduce((a, q) => a + q.supply, 0) / committedSquads.length),
      reserve: standing.length - committed.length,
      // X17: the holder's garrison deploys facing the lane it was last attacked from
      ...(state.rules.adaptiveGarrisons && fid === sys.owner ? (() => { const f = facingFor(sys.garrisonMemory, sys, state.systems); return f ? { facing: f } : {}; })() : {}),
      // X19: who waits in reserve, in order — a wave draws the next
      ...(state.rules.reserves ? { reserveUnits: standing.filter(u => !committed.includes(u)).map(u => ({ unitId: u.unitId, name: u.name, health: u.health, kit: u.kit })) } : {}),
      ...(arrival ? { arrival } : {}),
    };
  });
  const seed = `${state.seed}-${systemId}-${index}`;
  const opening = decideOpening(state, sys, ordered, seed);
  return {
    id: `battle-${index}`,
    seed,
    day: state.day,
    systemId,
    mode: opening.infiltration ? 'salvageRun' : 'deathmatch',
    map: { seed: sys.mapSeed, planetCount: sys.planetCount, planetSize: sys.planetSize, layout: sys.layout, hazardDensity: sys.hazardDensity },
    planets: sys.planets,
    structures: sys.owner ? sys.structures.map(b => ({ kind: b.kind, planetIndex: b.planetIndex, surfaceDir: b.surfaceDir, owner: sys.owner! })) : [],
    ...(sys.destroyedProps ? { destroyedProps: sys.destroyedProps } : {}),
    ...(state.rules.extraction ? { extraction: true } : {}),
    ...(state.rules.callIns ? { callIns: true } : {}),   // X11
    // X6: the window the battle is fought in
    ...(systemWeather(state, systemId) ? { weather: systemWeather(state, systemId)! } : {}),
    // X2: the sides' marks here cross down
    ...(state.rules.marksCrossDown && (state.marks ?? []).some(m => m.systemId === systemId && ordered.includes(m.faction)) ? { marks: (state.marks ?? []).filter(m => m.systemId === systemId && ordered.includes(m.faction)).map(m => ({ faction: m.faction, fire: m.fire, day: m.day })) } : {}),
    ...(state.rules.reserves ? { reservesArrive: true } : {}),
    sides,
    // The cautious side's doctrine ends the fight: a no-decision at its cap is its withdrawal (an infiltration needs room to sneak)
    maxRounds: Math.max(opening.infiltration ? 12 : 1, Math.min(...sides.map(s => s.doctrine.rounds))),
    lethality: { knockbackMultiplier: state.rules.knockbackMultiplier, fallDamageMultiplier: state.rules.fallDamageMultiplier },
    opening,
  };
}

/**
 * How the battle opens (RFC Q3, as ruled): awareness decides what is possible,
 * stance decides what is attempted, the battle is the roll. An infiltration
 * (alert layer on, defenders unaware and fighting as guards, attacker sneaking)
 * happens when the system is garrisoned, the attacker sneaks, and the holder
 * had no warning. `rules.openingBias` is the owner's override: it tilts a
 * seeded roll that is otherwise exactly the rule (0 → no roll at all).
 */
export function decideOpening(state: SectorState, sys: SectorSystem, orderedFactions: FactionId[], seed: string): BattleOpening {
  const defender = sys.owner && orderedFactions.includes(sys.owner) ? sys.owner : null;
  const attacker = orderedFactions.find(f => f !== defender) ?? null;
  const attackers = attacker ? squadsAt(state, sys.id).filter(q => q.faction === attacker && aliveUnits(q).length > 0) : [];
  const attackerStance: Stance = attackers.length > 0 && attackers.every(q => q.stance === 'sneak') ? 'sneak' : 'assault';
  // X17: a garrison that remembers a sneak is never unaware again here
  const rawFloor = alertFloor(sys, state.day, state.rules.alertDecayDays);
  const floor: AlertLevel = state.rules.adaptiveGarrisons && garrisonWatches(sys) && rawFloor === 'unaware' ? 'suspicious' : rawFloor;
  const defenderAware = !defender || attackers.length === 0 || !attackers.every(q => q.arrivedUnseen) || floor === 'alert';
  const garrisoned = !!defender && sys.garrisoned;
  const bias = state.rules.openingBias;
  let alertOpening = defenderAware || attackerStance !== 'sneak' || !garrisoned;
  if (bias !== 0 && garrisoned && attackerStance === 'sneak') {
    const p = Math.max(0, Math.min(1, (defenderAware ? 1 : 0) + bias));
    alertOpening = new SeededRandom(`${seed}-opening`).next() < p;
  }
  const infiltration = !alertOpening;
  return { defender, attacker, attackerStance, defenderAware, garrisoned, openingBias: bias, infiltration, startingAlert: infiltration ? (floor === 'alert' ? 'suspicious' : floor) : null };
}

/** Scar summary of a planet set: vertices dug below the generated surface, mass lost. */
export function scarsOf(planets: PlanetState[]): Omit<SystemScars, 'battles'> {
  let craterVertices = 0; let massLost = 0;
  for (const p of planets) {
    if (p.type !== 'planet') continue;
    const base = p.initialHeightmap;
    if (base) for (let i = 0; i < p.heightmap.length; i++) if (p.heightmap[i] < base[i] - 1e-6) craterVertices++;
    if (p.initialMass > 0) massLost += Math.max(0, 1 - p.mass / p.initialMass);
  }
  return { craterVertices, massLostFraction: massLost };
}

/**
 * The pending battle's outcome is in. A battle of no length lands at once (today's behaviour); a
 * battle with a length starts: it becomes a running record that lands at `day + battleDays`, the
 * system busy and its fighters held until then, while the clock moves on (lane D item 2).
 */
export function applyBattleOutcome(state: SectorState, outcome: BattleOutcome): SectorState {
  const setup = state.pending;
  if (!setup || setup.id !== outcome.battleId) throw new Error(`outcome ${outcome.battleId} does not match the pending battle`);
  const days = battleDays(outcome, state.rules);
  if (days <= EPS) return landOutcome(state, setup, outcome, state.day);
  const run: RunningBattle = { setup, outcome, startedDay: state.day, endsDay: state.day + days, tier: 'resolved' };
  const sys = systemById(state, setup.systemId);
  return {
    ...state,
    pending: null,
    running: [...(state.running ?? []), run],
    systems: state.systems.map(x => (x.id === sys.id ? { ...x, busyUntil: run.endsDay } : x)),
    log: [...state.log, { day: state.day, kind: 'battle', systemId: sys.id, text: `${setup.opening.infiltration ? 'Infiltration' : 'Battle'} begins at ${sys.name} (until day ${run.endsDay.toFixed(2)})` }],
  };
}

/**
 * The player enters a battle under way (owner, Oct 9: "still want a way for the user to enter a
 * battle and potentially change the outcome"). Its resolved outcome was a forecast: it is set
 * aside, and the battle is pending again, to be played and land on whatever the played battle
 * gives. Until the surrogate (62.9) can say how the battle stood partway through, it restarts
 * from its opening setup; its length counts from now.
 */
export function enterRunningBattle(state: SectorState, battleId: string): SectorState {
  const run = (state.running ?? []).find(r => r.setup.id === battleId);
  if (!run) throw new Error(`no battle ${battleId} is under way`);
  if (state.pending) throw new Error('another battle is waiting for a decision');
  const sys = systemById(state, run.setup.systemId);
  // A model battle is picked up where the model has it: who still stands, at what health (62.17 step 4).
  // A battle resolved at its start has no such state and restarts from its opening.
  let pending: BattleSetup = { ...run.setup, day: state.day, fidelity: [...(run.setup.fidelity ?? []), { day: state.day, tier: 'full', reason: 'enter' }] };
  if (run.tier === 'model' && run.model) {
    const now = runTo(run.model.start, battleSeconds(run, state.day), run.model.inputs);
    const health = new Map(now.sides.flatMap(x => x.units.filter(u => u.alive).map(u => [u.unitId, u.health] as const)));
    const inContact = now.sides.map(x => x.units.some(u => u.alive && u.engaged));
    pending = { ...pending, sides: pending.sides.map((side, i) => ({ ...side, contact: inContact[i] || undefined, units: side.units.filter(u => health.has(u.unitId)).map(u => ({ ...u, health: Math.max(1, Math.round(health.get(u.unitId)!)) })) })) };
    // the model's dead so far are dead in the sector now (the played battle starts from the survivors)
    const fallen = new Set(run.setup.sides.flatMap(side => side.units.filter(u => !health.has(u.unitId)).map(u => u.unitId)));
    if (fallen.size) state = { ...state, squads: state.squads.map(q => (q.units.some(u => fallen.has(u.id)) ? { ...q, units: q.units.map(u => (fallen.has(u.id) ? { ...u, alive: false, health: 0 } : u)) } : q)) };
  }
  return {
    ...state,
    running: (state.running ?? []).filter(r => r !== run),
    pending,
    log: [...state.log, { day: state.day, kind: 'battle', systemId: sys.id, text: `The battle at ${sys.name} is taken up in person${run.tier === 'model' ? ' where it stands' : '; its forecast is set aside'}` }],
  };
}

/**
 * 62.17 step 4: the pending battle unfolds in the battle model. Its record holds the model's start
 * and the inputs since; its end and outcome are derived (determinism: computing them now equals
 * stepping to them later) and re-derived when an input arrives. `model` overrides the starting state
 * (tests use slower params).
 */
export function startModelBattle(state: SectorState, model?: ModelBattle): SectorState {
  const setup = state.pending;
  if (!setup) throw new Error('no battle is pending');
  const start = model ?? modelFromSetup(setup);
  const surrogate: BattleSetup = { ...setup, fidelity: [...(setup.fidelity ?? []), { day: state.day, tier: 'surrogate', reason: 'budget' }] };
  const run = deriveModelRun({ setup: surrogate, outcome: null as unknown as BattleOutcome, startedDay: state.day, endsDay: state.day, tier: 'model', model: { start, inputs: [] } });
  const sys = systemById(state, setup.systemId);
  return {
    ...state,
    pending: null,
    running: [...(state.running ?? []), run],
    systems: state.systems.map(x => (x.id === sys.id ? { ...x, busyUntil: run.endsDay } : x)),
    log: [...state.log, { day: state.day, kind: 'battle', systemId: sys.id, text: `${setup.opening.infiltration ? 'Infiltration' : 'Battle'} begins at ${sys.name}` }],
  };
}

/**
 * The player leaves a battle they had entered (decision 111: leaving a full battle hands it to the
 * surrogate). The match's state is summarised into the model where it stands (who lives, at what
 * health, who is in contact), and the battle runs on as a model record from there; entering again
 * picks it up where the model has it. `seconds`: how far the full battle had run.
 */
export function leaveBattle(state: SectorState, finalState: GameState, seconds: number): SectorState {
  const pending = state.pending;
  if (!pending) throw new Error('no battle is pending');
  const setup: BattleSetup = { ...pending, fidelity: [...(pending.fidelity ?? []), { day: state.day, tier: 'surrogate', reason: 'leave' }] };
  const start = modelFromMatch(setup, finalState, seconds);
  // the record's clock: the battle began `seconds` ago, so its model time and the sector's agree
  const startedDay = state.day - seconds / SECONDS_PER_DAY;
  const run = deriveModelRun({ setup, outcome: null as unknown as BattleOutcome, startedDay, endsDay: state.day, tier: 'model', model: { start, inputs: [] } });
  const sys = systemById(state, setup.systemId);
  // what the full battle already settled: the dead are dead
  const dead = new Set(start.sides.flatMap(s => s.units.filter(u => !u.alive).map(u => u.unitId)));
  const squads = dead.size ? state.squads.map(q => (q.units.some(u => dead.has(u.id)) ? { ...q, units: q.units.map(u => (dead.has(u.id) ? { ...u, alive: false, health: 0 } : u)) } : q)) : state.squads;
  return {
    ...state,
    squads,
    pending: null,
    running: [...(state.running ?? []), run],
    systems: state.systems.map(x => (x.id === sys.id ? { ...x, busyUntil: run.endsDay } : x)),
    log: [...state.log, { day: state.day, kind: 'battle', systemId: sys.id, text: `The battle at ${sys.name} goes on without you (${Math.round(seconds)} s in)` }],
  };
}

/** The cross-tier seam, the sector's half: shells from outside land on one side of a battle under way. */
export function externalFire(state: SectorState, battleId: string, faction: FactionId, shells: number, lethality: number): SectorState {
  const r = (state.running ?? []).find(x => x.tier === 'model' && x.setup.id === battleId);
  if (!r) return state;
  const side = r.setup.sides.findIndex(s => s.faction === faction);
  if (side < 0) return state;
  return inputToRunning(state, r, { kind: 'fire', side: side as 0 | 1, shells, lethality });
}

/** Re-derive a model record's end and outcome from its start and inputs. */
function deriveModelRun(r: RunningBattle): RunningBattle {
  const end = runToEnd(r.model!.start, r.model!.inputs);
  return { ...r, outcome: { ...toOutcome(end, r.setup), chronicle: chronicleFromModel(r.model!.start, r.model!.inputs) }, endsDay: r.startedDay + end.t / SECONDS_PER_DAY };
}

/** Seconds into a running battle at `day`. */
function battleSeconds(r: RunningBattle, day: number): number {
  return Math.max(0, (day - r.startedDay) * SECONDS_PER_DAY);
}

/** Add an input to a running model battle at the current day and re-derive it. */
function inputToRunning(state: SectorState, r: RunningBattle, input: ModelInput, setup = r.setup): SectorState {
  const next = deriveModelRun({ ...r, setup, model: { start: r.model!.start, inputs: [...r.model!.inputs, { t: battleSeconds(r, state.day), input }] } });
  return {
    ...state,
    running: (state.running ?? []).map(x => (x === r ? next : x)),
    systems: state.systems.map(x => (x.id === r.setup.systemId ? { ...x, busyUntil: next.endsDay } : x)),
  };
}

/** The model battle a squad's faction is fighting at a system, and which side it is on. */
function modelBattleAt(state: SectorState, systemId: SystemId, faction: FactionId): { r: RunningBattle; side: 0 | 1 } | null {
  const r = (state.running ?? []).find(x => x.tier === 'model' && x.setup.systemId === systemId && x.setup.sides.some(s => s.faction === faction));
  if (!r) return null;
  return { r, side: r.setup.sides.findIndex(s => s.faction === faction) as 0 | 1 };
}

/** A model battle as it stands now (its start stepped to the sector's day with its inputs), or null for a resolved record. */
export function runningModelNow(state: SectorState, r: RunningBattle): ModelBattle | null {
  if (r.tier !== 'model' || !r.model) return null;
  return runTo(r.model.start, battleSeconds(r, state.day), r.model.inputs);
}

/** Travel days along the shortest path between two systems (0 for the same system; null when unreachable). */
export function pathDays(state: SectorState, from: SystemId, to: SystemId): number | null {
  if (from === to) return 0;
  const path = pathBetween(state, from, to);
  if (!path) return null;
  let days = 0;
  for (let i = 1; i < path.length; i++) days += laneBetween(state, path[i - 1], path[i])?.days ?? 0;
  return days;
}

/** Squads fighting a battle that has not landed yet: they hold where they are. */
export function squadsInBattle(state: SectorState): Set<SquadId> {
  return new Set((state.running ?? []).flatMap(r => r.setup.sides.flatMap(x => x.squadIds)));
}

/** Land every running battle that ends by `day` (in end order, ties by start). */
function landDue(state: SectorState): SectorState {
  let s = state;
  const due = (s.running ?? []).filter(r => r.endsDay <= s.day + EPS).sort((a, b) => a.endsDay - b.endsDay || a.startedDay - b.startedDay);
  for (const r of due) {
    s = { ...s, running: (s.running ?? []).filter(x => x !== r) };
    s = landOutcome(s, r.setup, r.outcome, r.startedDay);
    if (s.victor) break;
  }
  return s;
}

/**
 * Fold a battle's outcome back into the sector: unit health and deaths
 * (permadeath), empty squads disbanded, the system's planets replaced by the
 * scarred ones, ownership to the winner, and the clock freed. At the day it lands.
 */
// `startedDay`: the stand-off cooldown counts from the battle's start, as it did before battles ran over time.
function landOutcome(stateIn: SectorState, setup: BattleSetup, outcomeIn: BattleOutcome, startedDay: number): SectorState {
  let outcome = outcomeIn;
  const state = stateIn;
  // A capped infiltration with the attacker ahead on the objective is the attacker's quiet win (owner ruling 1)
  if (outcome.winner === null && setup.opening.infiltration && outcome.objectiveLeader && outcome.objectiveLeader === setup.opening.attacker) {
    outcome = { ...outcome, winner: outcome.objectiveLeader, reason: 'objective lead at the cap' };
  }
  const byUnit = new Map<UnitId, BattleOutcome['units'][number]>(outcome.units.map(u => [u.unitId, u]));
  // X19: reserves that arrived fought too — their standing lands on their units, whichever squad they are in
  for (const list of Object.values(outcome.reserveArrivals ?? {})) for (const a of list ?? []) byUnit.set(a.unitId, { unitId: a.unitId, alive: a.alive, health: a.health, kills: 0, shots: 0, hits: 0 });
  const fought = new Set(setup.sides.flatMap(s => s.squadIds));
  for (const q of state.squads) if (!fought.has(q.id) && q.units.some(u => byUnit.has(u.id))) fought.add(q.id);

  let squads: Squad[] = state.squads.map(q => {
    if (!fought.has(q.id)) return q;
    return {
      ...q,
      units: q.units.map(u => {
        const r = byUnit.get(u.id);
        if (!r) return u;
        return { ...u, alive: r.alive, health: r.alive ? Math.max(1, Math.round(r.health)) : 0, kills: u.kills + r.kills, battles: u.battles + 1 };
      }),
    };
  });
  // 62.3: what each side's survivors still carried goes back to its squads in order, up to what each brought
  if (outcome.dronesLeft) {
    for (const side of setup.sides) {
      let left = outcome.dronesLeft[side.faction] ?? 0;
      squads = squads.map(q => {
        if (!side.squadIds.includes(q.id)) return q;
        const keep = Math.min(q.drones, left);
        left -= keep;
        return { ...q, drones: keep };
      });
    }
  }
  // X20: what the living still carry comes back to the squads that fought
  if (outcome.ammoLeft) {
    squads = squads.map(q => (fought.has(q.id) && outcome.ammoLeft![q.faction] !== undefined ? { ...q, ammo: outcome.ammoLeft![q.faction] } : q));
  }
  const lost = squads.filter(q => fought.has(q.id) && !aliveUnits(q).length);
  squads = squads.filter(q => !lost.includes(q));

  const sys = systemById(state, setup.systemId);
  const scars: SystemScars = { battles: sys.scars.battles + 1, ...outcome.scars };
  // R13: what this battle destroyed stays destroyed here
  const union = (a: number[] = [], b: number[] = []) => [...new Set([...a, ...b])].sort((x, y) => x - y);
  const destroyedProps = outcome.destroyedProps
    ? { rocks: union(sys.destroyedProps?.rocks, outcome.destroyedProps.rocks), flora: union(sys.destroyedProps?.flora, outcome.destroyedProps.flora), hazards: union(sys.destroyedProps?.hazards, outcome.destroyedProps.hazards) }
    : sys.destroyedProps;
  // X2: the marks that crossed down are spent
  const marksLeft = (state.marks ?? []).filter(m => !(m.systemId === sys.id && setup.sides.some(x => x.faction === m.faction)));
  // X15: the wrecks this battle left lie here with the rest
  const wrecks = addWrecks(sys.wrecks, outcome.wrecks ?? wrecksOf(setup, outcome));
  // Structures the battle destroyed are gone (setup.structures and outcome.structuresSurvived line up by index)
  const standing = sys.structures.filter((_, i) => outcome.structuresSurvived?.[i] !== false);
  const destroyed = sys.structures.length - standing.length;
  let s: SectorState = {
    ...state,
    ...(state.marks ? { marks: marksLeft } : {}),
    squads,
    pending: state.pending && state.pending.id === setup.id ? null : state.pending,
    systems: state.systems.map(x => (x.id === sys.id ? { ...x, scars, planets: outcome.planets, structures: standing, ...(destroyedProps ? { destroyedProps } : {}), ...(wrecks ? { wrecks } : {}), lastBattleDay: startedDay, busyUntil: state.day, alert: { level: outcome.wentLoud ? 'alert' : 'suspicious', since: state.day } } : x)),
    battles: [...state.battles, { setup: stripPlanets(setup), outcome: stripOutcomePlanets(outcome), planetScars: scarLogsOfPlanets(outcome.planets) }],
    log: [
      ...state.log,
      {
        day: state.day, kind: 'battle', systemId: sys.id, faction: outcome.winner ?? undefined,
        text: `${setup.opening.infiltration ? 'Infiltration' : 'Battle'} at ${sys.name}: ${outcome.winner ? `${factionName(state, outcome.winner)} wins` : 'no decision'} in ${outcome.clock === 'action' ? `${Math.round(outcome.seconds ?? 0)} s` : `${outcome.rounds} rounds`} (${outcome.shots ? `${outcome.hits}/${outcome.shots} hits, ` : ''}${outcome.scars.craterVertices} scarred vertices${setup.opening.infiltration ? outcome.wentLoud ? ', went loud' : ', quiet' : ''}${destroyed ? `, ${destroyed} structure${destroyed > 1 ? 's' : ''} destroyed` : ''})`,
      },
      ...lost.map(q => ({ day: state.day, kind: 'squadLost' as const, text: `${q.name} is lost`, squadId: q.id, systemId: sys.id, faction: q.faction })),
      // X20: a shortage is named
      ...Object.entries(outcome.shortage ?? {}).map(([f, n]) => ({ day: state.day, kind: 'battle' as const, text: `${factionName(state, f)} ran dry at ${sys.name}: ${n} unit${n === 1 ? '' : 's'} with no rounds left`, systemId: sys.id, faction: f })),
    ],
  };
  // X1: what the battle leaves on the ground — laid before the sides look, so they learn it
  s = pruneTraces(addTraces(s, battleTraces(s, setup, outcome)));
  // Both sides saw the field and each other's survivors; a loud battle is heard by everyone
  s = observeBattle(s, sys.id, outcome.wentLoud ? factionsHearing(s, sys.id, setup.sides.map(x => x.faction)) : setup.sides.map(x => x.faction));
  // A loud battle found the sneakers; after a quiet one they are still unseen where they stand
  if (outcome.wentLoud) s = { ...s, squads: s.squads.map(q => (fought.has(q.id) && q.arrivedUnseen ? { ...q, arrivedUnseen: false } : q)) };
  // The winner holds the field (it stays garrisoned only for a holder that kept it); the loser's survivors retreat toward home.
  // 56.15: not while the loser still has units in reserve here — then this was one wave, and the next fights after the cooldown
  const committedIds = new Set(setup.sides.flatMap(x => x.units.map(u => u.unitId)));
  const loserReserve = outcome.winner
    ? s.squads.filter(q => q.at === sys.id && q.faction !== outcome.winner && setup.sides.some(x => x.faction === q.faction))
      .reduce((a, q) => a + aliveUnits(q).filter(u => !committedIds.has(u.id)).length, 0)
    : 0;
  if (outcome.winner && loserReserve > 0) {
    s = { ...s, log: [...s.log, { day: s.day, kind: 'battle', systemId: sys.id, faction: outcome.winner, text: `${factionName(state, outcome.winner)} wins the wave at ${sys.name}; ${loserReserve} in reserve fight next` }] };
  } else if (outcome.winner) {
    s = capture(s, sys.id, outcome.winner, sys.owner === outcome.winner && sys.garrisoned);
    // both sides saw who holds the field now (they looked before the capture; the first scenario's map kept the old owner)
    s = observeBattle(s, sys.id, setup.sides.map(x => x.faction));
    s = {
      ...s,
      squads: s.squads.map(q => {
        if (!fought.has(q.id) || q.faction === outcome.winner) return q;
        const home = s.factions.find(f => f.id === q.faction)?.home;
        return home && home !== q.at ? { ...q, order: { kind: 'move', to: home } } : q;
      }),
    };
  }
  // X17: the garrison that held remembers how it was attacked, and adapts
  if (s.rules.adaptiveGarrisons && setup.opening.defender && systemById(s, sys.id).owner === setup.opening.defender) {
    const memory = rememberAttack(sys.garrisonMemory, setup, outcome, s.day);
    if (memory) {
      s = { ...s, systems: s.systems.map(x => (x.id === sys.id ? { ...x, garrisonMemory: memory } : x)) };
      let note = memorySummary(memory, s);
      if (memory.adaptations.includes('refit')) {
        const kit = refitKitFor(memory);
        for (const q of s.squads.filter(x => x.faction === setup.opening.defender && x.at === sys.id && aliveUnits(x).length > 0)) {
          try { s = refitSquad(s, q.id, kit); } catch { note += ` (${q.name} could not afford ${kit})`; }
        }
      }
      s = { ...s, log: [...s.log, { day: s.day, kind: 'order', text: `The garrison at ${sys.name} adapts: ${note}`, systemId: sys.id, faction: setup.opening.defender }] };
    }
  }
  // 62.4: an attack is spent once fought
  if (s.squads.some(q => q.at === sys.id && q.order.kind === 'attack')) {
    s = { ...s, squads: s.squads.map(q => (q.at === sys.id && q.order.kind === 'attack' ? { ...q, order: { kind: 'hold' } } : q)) };
  }
  return s;
}

/** 56.16b: the day hostile squads standing in a system will fight again on their own (the cooldown or the last battle's length), or null. */
export function nextRefightDay(state: SectorState, systemId: SystemId): number | null {
  const sys = systemById(state, systemId);
  if (!standoffSystems(state).some(x => x.id === systemId)) return null;
  return refightDay(sys);
}

/** The outcome's planets as scar logs (save.ts has the same function; kept here to avoid a cycle). */
function scarLogsOfPlanets(planets: PlanetState[]): BattleRecord['planetScars'] {
  const out: NonNullable<BattleRecord['planetScars']> = [];
  planets.forEach((p, index) => {
    const base = p.initialHeightmap;
    const h: number[] = []; const s: number[] = [];
    if (base) for (let i = 0; i < p.heightmap.length; i++) if (p.heightmap[i] !== base[i]) h.push(i, p.heightmap[i]);
    if (p.scorch) for (let i = 0; i < p.scorch.length; i++) if (p.scorch[i] !== 0) s.push(i, p.scorch[i]);
    const gen = base ?? p.heightmap;
    if (h.length || s.length || p.mass !== p.initialMass || !p.alive) out.push({ index, n: gen.length, fp: terrainFingerprint(gen), h, s, mass: p.mass, alive: p.alive });
  });
  return out;
}

function stripPlanets(setup: BattleSetup): Omit<BattleSetup, 'planets'> {
  const { planets: _p, ...rest } = setup;
  return rest;
}

function stripOutcomePlanets(outcome: BattleOutcome): Omit<BattleOutcome, 'planets'> {
  const { planets: _p, ...rest } = outcome;
  return rest;
}

/** Log lines since `day`, oldest first. */
export function logSince(state: SectorState, day: number): SectorEvent[] {
  return state.log.filter(e => e.day >= day);
}

/** Seconds in a sector day. */
export const SECONDS_PER_DAY = 86_400;

/**
 * How long a battle occupies its system, in sector days — the one rule (R7). A turn-clock battle
 * costs `rounds × battleDaysPerRound` (a round is an abstraction the sector prices). An
 * action-clock battle ran in real time, and real time is the sector's time too (one world, one
 * clock): its seconds are seconds of the sector's day, whatever `battleDaysPerRound` says.
 * Outcomes saved before R7 carry no clock and are turn-clock outcomes.
 */
export function battleDays(outcome: Pick<BattleOutcome, 'rounds' | 'clock' | 'seconds'>, rules: Pick<SectorRules, 'battleDaysPerRound'>): number {
  if (outcome.clock === 'action') return (outcome.seconds ?? 0) / SECONDS_PER_DAY;
  return outcome.rounds * rules.battleDaysPerRound;
}

/** A battle's length as a person reads it: "12 rounds" or "2 min 20 s". */
export function battleLengthText(outcome: Pick<BattleOutcome, 'rounds' | 'clock' | 'seconds'>): string {
  if (outcome.clock !== 'action') return `${outcome.rounds} round${outcome.rounds === 1 ? '' : 's'}`;
  const sec = Math.round(outcome.seconds ?? 0);
  return sec >= 60 ? `${Math.floor(sec / 60)} min ${sec % 60} s` : `${sec} s`;
}

